// import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Switch,
  Tabs,
} from "antd";
import { Text } from "../../theme/typography";
import { topologies } from "../../services/fixtures/topologies";
import { IProject, ParamField } from "../../models/project";
import { TYPOGRAPHY_SIZES } from "../../theme/constants";
import {
  defaultProject,
  DraftProjectContext,
  ProjectContext,
  UserContext,
} from "../../services/contexts";
import { useUpdatePartialProjectMutation } from "../../redux/slices/project";
import {
  DOUBLE_CAGE_LOWER_BLOCK_FIRST_KEY,
  DOUBLE_CAGE_UPPER_BLOCK_FIRST_KEY,
  INDUCTION_MACHINE_TOPOLOGY,
  ROTOR_SPECIFICATION_GROUP,
  shouldShowDoubleCageRotorDividers,
} from "../../services/inductionDoubleCageRotorDividers";

interface IGroup {
  name: string;
  parameters: IParameter[];
}

type Rule =
  | { field: string; equals: any }
  | { field: string; in: any[] }
  | { all: Rule[] }
  | { any: Rule[] }
  | { not: Rule };

interface IParameter {
  parameter: string;
  key: string;
  defaultValue: string | number | boolean;
  description: string;
  type: string;
  options?: string[];
  visibleWhen?: Rule;
  /** When the rule matches, only these options are allowed (option type). */
  restrictOptionsWhen?: { when: Rule; options: string[] };
  /** When the rule matches, the field is forced to this value (any type). */
  valueWhen?: { when: Rule; value: string | number | boolean };
  /** Min value for number type (default 0 when omitted). */
  min?: number;
  /** Max value for number type (default 1000 when omitted). */
  max?: number;
  dependsOn?: string[];
  formula?: (values: Record<string, number>) => number;  
}

interface ITopology {
  topology: string;
  groups: IGroup[];
  description: string;
}

interface IProps {
  onClose: () => void;
  openModal: boolean;
}

const isVisible = (rule: Rule | undefined, values: Record<string, any>): boolean => {
  if (!rule) return true;

  if ("field" in rule && "equals" in rule) return values[rule.field] === rule.equals;
  if ("field" in rule && "in" in rule) return rule.in.includes(values[rule.field]);

  if ("all" in rule) return rule.all.every((r) => isVisible(r, values));
  if ("any" in rule) return rule.any.some((r) => isVisible(r, values));
  if ("not" in rule) return !isVisible(rule.not, values);

  return true;
};

/** Effective options for an option param: restricted list when restrictOptionsWhen matches. */
const getEffectiveOptions = (param: IParameter, values: Record<string, any>): string[] | undefined => {
  const opts = param.options;
  if (!opts?.length) return opts;
  const r = param.restrictOptionsWhen;
  if (!r || !isVisible(r.when, values)) return opts;
  return r.options.filter((o) => opts.includes(o));
};

const normalizeOption = (dv: any, opts?: string[]) => {
  if (!opts || opts.length === 0) return dv ?? undefined;

  const options = opts.map((o) => String(o).trim());
  if (dv == null || dv === "") return options[0];

  const raw = String(dv).trim();
  if (options.includes(raw)) return raw;

  const low = raw.toLowerCase();
  const found = options.find((o) => o.toLowerCase() === low);
  return found ?? options[0];
};

const buildDefaultsFromTopology = (topology?: ITopology): Record<string, any> => {
  if (!topology) return {};
  const initials: Record<string, any> = {};

  topology.groups.forEach((g) => {
    g.parameters.forEach((p) => {
      const t = String(p.type).trim().toLowerCase();
      if (t === "computed") return;
      if (t === "number") {
        const n = typeof p.defaultValue === "number" ? p.defaultValue : Number(p.defaultValue);
        initials[p.key] = Number.isFinite(n) ? n : 0;
      } else if (t === "boolean") {
        initials[p.key] = Boolean(p.defaultValue);
      } else if (t === "option") {
        initials[p.key] = normalizeOption(p.defaultValue, p.options);
      } else {
        initials[p.key] = p.defaultValue ?? "";
      }
    });
  });

  topology.groups.forEach((g) => {
    g.parameters.forEach((p) => {
      if (String(p.type).trim().toLowerCase() !== "computed") return;
      if (p.formula && p.dependsOn?.every((dep) => initials[dep] !== undefined)) {
        initials[p.key] = p.formula(initials as Record<string, number>);
      } else {
        initials[p.key] = typeof p.defaultValue === "number" ? p.defaultValue : 1;
      }
    });
  });

  return initials;
};

const normalizeProjectParamsToTopology = (
  topology: ITopology | undefined,
  params: Record<string, any> | undefined
): Record<string, any> => {
  if (!topology) return params ?? {};

  // Start from topology defaults
  const base = buildDefaultsFromTopology(topology);

  // Overlay project params, but normalize option fields to match options exactly
  const out = { ...base, ...(params ?? {}) };

  topology.groups.forEach((g) => {
    g.parameters.forEach((p) => {
      const t = String(p.type).trim().toLowerCase();
      if (t === "option") {
        out[p.key] = normalizeOption(out[p.key], p.options);
      } else if (t === "number") {
        const n = typeof out[p.key] === "number" ? out[p.key] : Number(out[p.key]);
        out[p.key] = Number.isFinite(n) ? n : 0;
      } else if (t === "boolean") {
        out[p.key] = Boolean(out[p.key]);
      }
    });
  });

  return out;
};

const EditProjectModal: FC<IProps> = ({ onClose, openModal }) => {
  const { user } = useContext(UserContext);
  const [form] = Form.useForm<Partial<ParamField>>();
  const [selectedTopology, setSelectedTopology] = useState<ITopology>();
  const [api, contextHolder] = notification.useNotification();

  const { setDraftProject } = useContext(DraftProjectContext);
  const { selectedProject, setSelectedProject } = useContext(ProjectContext);
  const [updatePartialProject, { isLoading: isUpdatingProject }] = useUpdatePartialProjectMutation();

  const allValues = Form.useWatch([], form) || {};

  useEffect(() => {
    const filteredTopology = topologies.find((item) => item.topology === selectedProject?.topology);
    setSelectedTopology(filteredTopology as ITopology | undefined);
  }, [selectedProject]);

  const onSelectTopology = (value: string) => {
    const topology = topologies.find((item) => item.topology === value);
    if (topology) setSelectedTopology(topology as ITopology);
  };

  const getComponentByField = (
    param: IParameter,
    opts?: { effectiveOptions?: string[]; disabled?: boolean }
  ) => {
    const t = String(param.type).trim().toLowerCase();
    const options = opts?.effectiveOptions ?? param.options;
    const disabled = opts?.disabled ?? false;
    switch (t) {
      case "computed":
        return (
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Auto-calculated"
            disabled={disabled}
          />
        );
      case "number":
        return (
          <InputNumber
            min={param.min ?? 0}
            max={param.max ?? 1000000}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        );
      case "boolean":
        return <Switch disabled={disabled} />;
      case "option":
        return (
          <Select
            style={{ width: "100%" }}
            options={options?.map((o) => ({ label: o, value: o }))}
            disabled={disabled}
          />
        );
      default:
        return <Input placeholder={param.parameter} disabled={disabled} />;
    }
  };

  const handleSubmit = async (values: Partial<ParamField>) => {
    try {
      if (!user?.id) return;
      if (!selectedProject?.id) return;

      (values as any)["stator_winding_layer"] = Number((values as any)["stator_winding_layer"]);
      (values as any)["rotor_winding_layer"] = Number((values as any)["rotor_winding_layer"]);
      (values as any)["number_of_rotor_flux_barriers"] = Number((values as any)["number_of_rotor_flux_barriers"]);

      if (String(values["coil_throw"]) === "") {
        (values as any)["coil_throw"] =  Math.round((values as any)["number_of_stator_slots"] / (2 * (values as any)["number_of_pole_pairs"]));
      }

      const payload: Partial<IProject> = {
        topology: selectedTopology?.topology,
        owner: user.id,
        params: values,
        id: selectedProject.id,
      };

      const projectResponse: any = await updatePartialProject(payload).unwrap();
      // Mantener `selectedProject` para que la app sepa cuál proyecto está activo.
      setSelectedProject(projectResponse);
      // Si veníamos editando un "draft", limpiar el draft para evitar estados inconsistentes.
      setDraftProject(defaultProject);
      onClose();
    } catch (error: any) {
      api.error({
        message: <Text weight={600}>Something went wrong while editing your project</Text>,
        description: (
          <Flex vertical>
            <Text>Please check your values:</Text>
            <Text weight={600}>{error?.data?.detail}</Text>
          </Flex>
        ),
        placement: "topRight",
      });
    }
  };

  // ✅ initialValues must be ready before render (and will apply on mount)
  const initialValues = useMemo(() => {
    if (!selectedTopology) return {};

    const sameTopology = selectedProject?.topology === selectedTopology.topology;

    if (sameTopology) {
      return normalizeProjectParamsToTopology(selectedTopology, selectedProject?.params as any);
    }

    return buildDefaultsFromTopology(selectedTopology);
  }, [selectedTopology, selectedProject]);

  // ✅ Sync restrictOptionsWhen: when active, ensure value is in restricted list; sync valueWhen: set field to forced value
  useEffect(() => {
    if (!selectedTopology || !openModal) return;
    const values = allValues as Record<string, any>;
    selectedTopology.groups.forEach((g) => {
      g.parameters.forEach((p) => {
        if (p.restrictOptionsWhen && isVisible(p.restrictOptionsWhen.when, values)) {
          const allowed = p.restrictOptionsWhen.options;
          const current = values[p.key];
          if (allowed.length && current != null && !allowed.includes(String(current).trim())) {
            form.setFieldValue(p.key as keyof ParamField, allowed[0]);
          }
        }
        if (p.valueWhen && isVisible(p.valueWhen.when, values)) {
          form.setFieldValue(p.key as keyof ParamField, p.valueWhen.value);
        }
      });
    });
  }, [selectedTopology, openModal, allValues, form]);

  // ✅Auto-recalculate computed fields whenever any dependency changes
  const prevDepsRef = useRef<Record<string, number>>({});
  
  useEffect(() => {
    if (!selectedTopology || !openModal) return;
    const values = allValues as Record<string, number>;
    selectedTopology.groups.forEach((g) => {
      g.parameters.forEach((p) => {
        if (String(p.type).trim().toLowerCase() !== "computed") return;
        if (!p.formula || !p.dependsOn) return;

        const allDepsReady = p.dependsOn.every(
          (dep) => values[dep] !== undefined && !isNaN(Number(values[dep]))
        );
        if (!allDepsReady) return;

        const depsChanged = p.dependsOn.some(
          (dep) => prevDepsRef.current[dep] !== values[dep]
        );

        if (depsChanged) {
          const computed = p.formula(values);
          form.setFieldValue(p.key as keyof ParamField, computed);
        }

        p.dependsOn.forEach((dep) => {
          prevDepsRef.current[dep] = values[dep];
        });
      });
    });
  }, [selectedTopology, openModal, allValues, form]);

  useEffect(() => {
    if (!openModal || !selectedTopology) return;
    const timer = setTimeout(() => {
      const values = form.getFieldsValue() as Record<string, number>;

      selectedTopology.groups.forEach((g) => {
        g.parameters.forEach((p) => {
          if (String(p.type).trim().toLowerCase() !== "computed") return;
          if (!p.formula || !p.dependsOn) return;

          const allDepsReady = p.dependsOn.every(
            (dep) => values[dep] !== undefined && !isNaN(Number(values[dep]))
          );
          if (!allDepsReady) return;

          const computed = p.formula(values);
          form.setFieldValue(p.key as keyof ParamField, computed);

          p.dependsOn.forEach((dep) => {
            prevDepsRef.current[dep] = values[dep];
          });
        });
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [openModal, selectedTopology, form]);  

  return (
    <Modal
      onCancel={onClose}
      open={openModal}
      width={{ xs: "90%", sm: "90%", md: "90%", lg: "90%", xl: "50%", xxl: "40%" }}
      footer={null}
    >
      {contextHolder}

      <Flex vertical gap="large" style={{ paddingTop: 50 }}>
        <Text weight={700} style={{ marginBottom: -15 }}>
          Select a Topology
        </Text>

        <Select
          showSearch
          value={selectedTopology?.topology}
          style={{ width: "100%" }}
          allowClear
          placeholder="Select a Topology"
          // ✅ use onChange
          onChange={(value) => value && onSelectTopology(value)}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={topologies.map((item) => ({ value: item.topology, label: item.topology }))}
        />

        {selectedTopology ? (
          <Form
            key={`${selectedProject?.id ?? "new"}-${selectedTopology.topology}`} // ✅ remount -> defaults work + rules stable
            form={form}
            name="edit_project_form"
            autoComplete="off"
            layout="vertical"
            requiredMark={false}
            initialValues={initialValues}
            onFinish={handleSubmit}
          >
            <Tabs
              key={selectedTopology.topology}
              defaultActiveKey={selectedTopology.groups[0].name}
              destroyOnHidden={false}
              style={{ width: "100%" }}
              items={selectedTopology.groups.map((group) => {
                const topologyName = selectedTopology.topology;
                const useDoubleCageDividers =
                  topologyName === INDUCTION_MACHINE_TOPOLOGY && shouldShowDoubleCageRotorDividers(allValues);
                const upperIdx = group.parameters.findIndex((p) => p.key === DOUBLE_CAGE_UPPER_BLOCK_FIRST_KEY);
                const lowerIdx = group.parameters.findIndex((p) => p.key === DOUBLE_CAGE_LOWER_BLOCK_FIRST_KEY);
                const showDividers =
                  useDoubleCageDividers &&
                  group.name === ROTOR_SPECIFICATION_GROUP &&
                  upperIdx !== -1 &&
                  lowerIdx !== -1;

                let upperDividerPlaced = false;
                let lowerDividerPlaced = false;

                return {
                  key: group.name,
                  label: group.name,
                  forceRender: true,
                  children: (
                    <Row gutter={18} style={{ width: "100%" }}>
                      {group.parameters.flatMap((param, idx) => {
                        const visible = isVisible(param.visibleWhen, allValues);
                        const effectiveOptions = getEffectiveOptions(param, allValues);
                        const valueWhenActive =
                          param.valueWhen != null && isVisible(param.valueWhen.when, allValues);

                        const out: React.ReactNode[] = [];

                        if (showDividers && visible) {
                          if (!upperDividerPlaced && idx >= upperIdx && idx < lowerIdx) {
                            upperDividerPlaced = true;
                            out.push(
                              <Col span={24} key={`${group.name}-divider-upper-cage`}>
                                <Divider orientation="left" plain>
                                  <Text size={TYPOGRAPHY_SIZES.SMALL} weight={700}>
                                    Upper cage
                                  </Text>
                                </Divider>
                              </Col>
                            );
                          }
                          if (!lowerDividerPlaced && idx >= lowerIdx) {
                            lowerDividerPlaced = true;
                            out.push(
                              <Col span={24} key={`${group.name}-divider-lower-cage`}>
                                <Divider orientation="left" plain>
                                  <Text size={TYPOGRAPHY_SIZES.SMALL} weight={700}>
                                    Lower cage
                                  </Text>
                                </Divider>
                              </Col>
                            );
                          }
                        }

                        if (!visible) return out;

                        out.push(
                          <Col key={param.key} span={8}>
                            <Form.Item<Partial<ParamField>>
                              preserve={false}
                              name={param.key as keyof ParamField}
                              label={
                                <Text size={TYPOGRAPHY_SIZES.SMALL} weight={700}>
                                  {param.parameter}
                                </Text>
                              }
                              tooltip={param.description}
                              valuePropName={
                                String(param.type).trim().toLowerCase() === "boolean"
                                  ? "checked"
                                  : "value"
                              }
                            >
                              {getComponentByField(param, {
                                effectiveOptions,
                                disabled: valueWhenActive,
                              })}
                            </Form.Item>
                          </Col>
                        );
                        return out;
                      })}
                    </Row>
                  ),
                };
              })}
            />

            <Flex justify="end">
              <Form.Item style={{ marginTop: 50 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isUpdatingProject}
                  disabled={isUpdatingProject}
                  style={{ width: "100%" }}
                >
                  Edit Project
                </Button>
              </Form.Item>
            </Flex>
          </Form>
        ) : null}
      </Flex>
    </Modal>
  );
};

export default EditProjectModal;